import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  FileDownloadOutlined,
  Recommend,
  Schedule,
} from '@mui/icons-material';
import {
  fmt,
  getDisplayText,
  getDisplayTone,
  tdSx,
  thSx,
} from './shared';
import { colors } from '../../theme';

export default function RecommendationsSection({
  t,
  lang,
  loading,
  recItems,
  recommendations,
  exportRef,
  exportingReportImage,
  handleExportReportImage,
  hasResults,
  openTickerDrawer,
}) {
  if (loading || recItems.length === 0) {
    return null;
  }

  return (
    <Box ref={exportRef} sx={{ bgcolor: colors.background.paper, borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, mb: 2.5, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
            <Recommend sx={{ fontSize: 14, color: colors.gray[500] }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.gray[600] }}>{t('vpa.rec.title')}</Typography>
            <Chip label={recItems.length} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(colors.info.main, 0.08), color: colors.info.main }} />
          </Box>
          <Button
            variant="outlined"
            data-html2canvas-ignore="true"
            startIcon={exportingReportImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 16 }} />}
            onClick={handleExportReportImage}
            disabled={loading || exportingReportImage || !hasResults}
            sx={{
              textTransform: 'none', fontSize: 12.5, fontWeight: 600, borderRadius: '6px',
              color: colors.gray[600], borderColor: colors.gray[300], px: 1.6, py: 0.45, bgcolor: colors.background.paper,
              '&:hover': { bgcolor: colors.gray[50], borderColor: colors.gray[400] },
              '&:disabled': { bgcolor: colors.gray[100], color: colors.gray[400], borderColor: colors.gray[200] },
            }}
          >
            {exportingReportImage ? t('common.exportingImage', 'Đang xuất ảnh...') : t('common.exportImage', 'Xuất ảnh')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          {[
            { Icon: CheckCircle, label: t('vpa.rec.ready'), desc: t('vpa.rec.readyDesc'), count: recommendations.counts?.ready || 0, color: colors.success.main },
            { Icon: Schedule, label: t('vpa.rec.watch'), desc: t('vpa.rec.watchDesc'), count: recommendations.counts?.watch || 0, color: colors.warning.main },
          ].map((group) => (
            <Box key={group.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <group.Icon sx={{ fontSize: 13, color: group.color }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: group.color }}>{group.count} {group.label}</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: colors.gray[400] }}>— {group.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 90 }}>{t('vpa.rec.headers.status')}</TableCell>
              <TableCell sx={thSx}>{t('vpa.rec.headers.ticker')}</TableCell>
              <TableCell sx={thSx}>{t('vpa.rec.headers.sector')}</TableCell>
              <TableCell sx={thSx}>{t('vpa.rec.headers.setup')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vpa.rec.headers.price')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vpa.rec.headers.entry')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vpa.rec.headers.stop')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vpa.rec.headers.target1')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vpa.rec.headers.target2')}</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vpa.rec.headers.score')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recItems.map((signal, idx) => {
              const recommendationDisplay = signal.display?.recommendation || {};
              const statusDisplay = recommendationDisplay.status || null;
              const setupDisplay = recommendationDisplay.setup || null;
              const stopDisplay = recommendationDisplay.stop_loss || null;
              const target1Display = recommendationDisplay.target_1 || null;
              const target2Display = recommendationDisplay.target_2 || null;
              const scoreDisplay = recommendationDisplay.score || null;
              const recommendationTone = getDisplayTone(statusDisplay?.tone);
              const StatusIcon = signal.recommendation_key === 'ready' ? CheckCircle : Schedule;
              return (
                <TableRow
                  key={`${signal.ticker}-${idx}`}
                  hover
                  sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: alpha(recommendationTone.color, 0.02) }, '&:hover': { bgcolor: alpha(recommendationTone.color, 0.06) } }}
                  onClick={() => openTickerDrawer(signal)}
                >
                  <TableCell sx={tdSx}>
                    <Tooltip title={getDisplayText(statusDisplay, 'sub', t, '')} arrow placement="right">
                      <Chip
                        icon={<StatusIcon sx={{ fontSize: '13px !important', color: `${recommendationTone.color} !important` }} />}
                        label={getDisplayText(statusDisplay, 'value', t)}
                        size="small"
                        sx={{
                          height: 22, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: recommendationTone.bg, color: recommendationTone.color,
                          border: `1px solid ${alpha(recommendationTone.color, 0.2)}`,
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: colors.gray[900] }}>{signal.ticker}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.7rem', color: colors.gray[500] }}>{signal.sector_name?.[lang] || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.7rem', color: getDisplayTone(setupDisplay?.tone).color }}>{getDisplayText(setupDisplay, 'value', t)}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[900] }}>{fmt(signal.close)}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.info.main, fontWeight: 700 }}>{signal.entry_ref ? fmt(signal.entry_ref) : '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.error.main, fontWeight: 700 }}>{signal.stop_loss ? fmt(signal.stop_loss) : '—'}</Typography>
                      {getDisplayText(stopDisplay, 'sub', t, null) && <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400] }}>{getDisplayText(stopDisplay, 'sub', t, null)}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.success.main, fontWeight: 700 }}>{signal.target_1 ? fmt(signal.target_1) : '—'}</Typography>
                      {getDisplayText(target1Display, 'sub', t, null) && <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400] }}>{getDisplayText(target1Display, 'sub', t, null)}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.success.dark, fontWeight: 700 }}>{signal.target_2 ? fmt(signal.target_2) : '—'}</Typography>
                      {getDisplayText(target2Display, 'sub', t, null) && <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400] }}>{getDisplayText(target2Display, 'sub', t, null)}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, fontFamily: 'monospace', color: getDisplayTone(scoreDisplay?.tone).color }}>
                      {getDisplayText(scoreDisplay, 'value', t, '—')}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
